import { Fragment } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, Line, Polygon, Text as SvgText } from 'react-native-svg';
import { Radar } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type { DimensionMetricViewModel } from '../../types/learningAnalysis';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type DimensionChartProps = {
  metrics: DimensionMetricViewModel[];
};

type ChartPoint = {
  x: number;
  y: number;
};

const CHART_SIZE = 252;
const CENTER = CHART_SIZE / 2;
const MAX_RADIUS = 86;
const GRID_LEVELS = [0.25, 0.5, 0.75, 1];

const DEFAULT_DIMENSION_LABELS = ['知识掌握', '正确率', '专注度', '学习时长', '进步率'];

const clampScore = (score: number): number => {
  if (!Number.isFinite(score)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(score)));
};

const getPoint = (index: number, radius: number): ChartPoint => {
  const angle = -Math.PI / 2 + (index * 2 * Math.PI) / DEFAULT_DIMENSION_LABELS.length;
  return {
    x: CENTER + Math.cos(angle) * radius,
    y: CENTER + Math.sin(angle) * radius,
  };
};

const pointsToString = (points: ChartPoint[]): string =>
  points.map((point) => `${point.x},${point.y}`).join(' ');

export function DimensionChart({ metrics }: DimensionChartProps) {
  const normalizedMetrics = DEFAULT_DIMENSION_LABELS.map((label, index) => {
    const metric = metrics.find((item) => item.label === label || item.id === label);
    return {
      id: metric?.id ?? `dimension-${index + 1}`,
      label,
      score: metric ? clampScore(metric.score) : null,
    };
  });
  const hasCompleteData = normalizedMetrics.every((metric) => metric.score !== null);

  if (!hasCompleteData) {
    return (
      <AppCard style={styles.card}>
        <View style={styles.header}>
          <Radar size={18} color={tokens.colors.starCyan} />
          <AppText variant="body" weight="bold" style={styles.title}>
            五维学情图表
          </AppText>
        </View>
        <View style={styles.emptyState}>
          <AppText variant="body" weight="bold" style={styles.emptyTitle}>
            数据不足
          </AppText>
          <AppText variant="caption" style={styles.emptyCopy}>
            完成几次练习后，这里会生成知识掌握、正确率、专注度、学习时长、进步率的闭合多边形。
          </AppText>
        </View>
      </AppCard>
    );
  }

  const chartMetrics = normalizedMetrics.map((metric) => ({
    ...metric,
    score: metric.score ?? 0,
  }));
  const gridPoints = GRID_LEVELS.map((level) =>
    pointsToString(DEFAULT_DIMENSION_LABELS.map((_, index) => getPoint(index, MAX_RADIUS * level)))
  );
  const dataPoints = pointsToString(
    chartMetrics.map((metric, index) => getPoint(index, (MAX_RADIUS * metric.score) / 100))
  );

  return (
    <AppCard style={styles.card}>
      <View style={styles.header}>
        <Radar size={18} color={tokens.colors.starCyan} />
        <View style={styles.headerCopy}>
          <AppText variant="body" weight="bold" style={styles.title}>
            五维学情图表
          </AppText>
          <AppText variant="caption" style={styles.subtitle}>
            DimensionChart 使用五维数组驱动雷达图
          </AppText>
        </View>
      </View>

      <View
        accessibilityRole="image"
        accessibilityLabel={`五维学情闭合多边形，${chartMetrics
          .map((metric) => `${metric.label}${metric.score}分`)
          .join('，')}`}
        style={styles.chartWrap}
      >
        <Svg width={CHART_SIZE} height={CHART_SIZE}>
          {gridPoints.map((gridPoint, index) => (
            <Polygon
              key={`grid-${GRID_LEVELS[index]}`}
              points={gridPoint}
              fill="none"
              stroke={tokens.colors.starLine}
              strokeOpacity={0.42}
              strokeWidth={1}
            />
          ))}
          {DEFAULT_DIMENSION_LABELS.map((label, index) => {
            const edgePoint = getPoint(index, MAX_RADIUS);
            const labelPoint = getPoint(index, MAX_RADIUS + 24);
            return (
              <Fragment key={label}>
                <Line
                  x1={CENTER}
                  y1={CENTER}
                  x2={edgePoint.x}
                  y2={edgePoint.y}
                  stroke={tokens.colors.starLine}
                  strokeOpacity={0.45}
                  strokeWidth={1}
                />
                <SvgText
                  x={labelPoint.x}
                  y={labelPoint.y}
                  fill={tokens.colors.starText}
                  fontSize={11}
                  fontWeight="700"
                  textAnchor="middle"
                  alignmentBaseline="middle"
                >
                  {label}
                </SvgText>
              </Fragment>
            );
          })}
          <Polygon
            points={dataPoints}
            fill={tokens.colors.starCyan}
            fillOpacity={0.2}
            stroke={tokens.colors.starCyan}
            strokeWidth={3}
          />
          {chartMetrics.map((metric, index) => {
            const point = getPoint(index, (MAX_RADIUS * metric.score) / 100);
            return (
              <Circle
                key={metric.id}
                cx={point.x}
                cy={point.y}
                r={5}
                fill={tokens.colors.deepSpace}
                stroke={tokens.colors.starCyan}
                strokeWidth={2}
              />
            );
          })}
        </Svg>
      </View>

      <View style={styles.legendGrid}>
        {chartMetrics.map((metric) => (
          <View key={metric.id} style={styles.legendItem}>
            <AppText variant="caption" style={styles.legendLabel}>
              {metric.label}
            </AppText>
            <AppText variant="caption" weight="bold" style={styles.legendScore}>
              {metric.score}
            </AppText>
          </View>
        ))}
      </View>
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.md,
    backgroundColor: tokens.colors.deepSpace,
    borderColor: tokens.colors.starLine,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
    gap: 2,
  },
  title: {
    color: tokens.colors.starText,
  },
  subtitle: {
    color: tokens.colors.starMuted,
  },
  chartWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: CHART_SIZE,
  },
  emptyState: {
    minHeight: 136,
    borderRadius: tokens.radius.lg,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.starLine,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.lg,
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.deepSpaceAlt,
  },
  emptyTitle: {
    color: tokens.colors.starText,
  },
  emptyCopy: {
    color: tokens.colors.starMuted,
    textAlign: 'center',
  },
  legendGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  legendItem: {
    minWidth: 92,
    flex: 1,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.starLine,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
    backgroundColor: tokens.colors.deepSpaceAlt,
  },
  legendLabel: {
    color: tokens.colors.starMuted,
  },
  legendScore: {
    color: tokens.colors.starText,
  },
});
